var _excluded = ["id", "className", "options", "label", "required", "type", "helpText", "errorText", "warningText", "successText", "hasError", "hasWarning", "isHorizontal", "isInverse", "onContainerSize", "utils"],
  _excluded2 = ["id", "name", "text", "value"],
  _excluded3 = ["id", "name", "text", "value"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import nodeOfType from '../../utilities/types-of-node';
import InverseContext from '../../utilities/inverse-context';
import FormOption from '../form-option';
import Label from '../label';
import Hint from '../hint';
import Type from '../type';
import HorizontalList from '../horizontal-list';
import VerticalList from '../vertical-list';
import generateId from '../../utilities/generateID';
import ContainerQuery from './../container-query';
import { StyledCacheWrapper } from '../../styles';
import StyledFormOptionGroup from './form-option-group.styles';
var formOptionGroupPropTypes = {
  /** ID of the FormOptionGroup */
  id: PropTypes.string,
  /** Option array */
  options: PropTypes.arrayOf(PropTypes.shape({
    /** Unique Id */
    id: PropTypes.string,
    /** Input name */
    name: PropTypes.string,
    /** Input value */
    value: PropTypes.string,
    /** Input label text */
    text: PropTypes.oneOfType([PropTypes.string, nodeOfType([Type])])
  })),
  /** The label of the FormOptionGroup. */
  label: PropTypes.oneOfType([PropTypes.string, nodeOfType([Type])]),
  /** Specify a type to render all options of the same type e.g. type='radio', type='checkbox' */
  type: PropTypes.string,
  /** Add custom CSS classes to component. */
  className: PropTypes.string,
  /** Specifies whether the button group is required. */
  required: PropTypes.bool,
  /** Toggles the error state. */
  hasError: PropTypes.bool,
  /** Toggles the warning error state. */
  hasWarning: PropTypes.bool,
  /** Unobtrusively styled text to appear below button group to aid user input. */
  helpText: PropTypes.node,
  /** Display error text to aid user correction when validated. */
  errorText: PropTypes.node,
  /** Unobtrusively styled warning text to appear below formField to aid user input. */
  warningText: PropTypes.node,
  /** Unobtrusively styled green text to appear below formField to recommend user input. */
  successText: PropTypes.node,
  /** Component design with horizontal layout. */
  isHorizontal: PropTypes.bool,
  /** Component design with dark background. */
  isInverse: PropTypes.oneOf(['auto', true, false]),
  /** Callback for when component resizes */
  onContainerSize: PropTypes.func,
  /** Add utility props for styling */
  utils: PropTypes.object
};
var formOptionGroupDefaultProps = {
  id: '',
  options: [],
  label: '',
  type: 'checkbox',
  className: '',
  required: false,
  hasError: false,
  hasWarning: false,
  helpText: '',
  errorText: '',
  warningText: '',
  successText: '',
  isHorizontal: false,
  isInverse: 'auto',
  onContainerSize: function onContainerSize() {},
  utils: {}
};
var generateDescribedBy = function generateDescribedBy(hasError, errorText, hasWarning, warningText, successText, useId) {
  var optionStatusId = '';
  if (hasError && errorText !== '') {
    optionStatusId = "".concat(useId, "_error");
  } else if (hasWarning && warningText !== '') {
    optionStatusId = "".concat(useId, "_warning");
  } else if (successText !== '') {
    optionStatusId = "".concat(useId, "_success");
  }
  var hintID = !hasError || !hasWarning && "".concat(useId, "-hint");
  return optionStatusId + ' ' + hintID;
};
var renderHelp = function renderHelp(errorText, useId, hasError, hasWarning, successText, warningText, helpText) {
  return /*#__PURE__*/React.createElement(React.Fragment, null, successText || errorText && hasError || warningText && hasWarning ? null : /*#__PURE__*/React.createElement(React.Fragment, null, helpText && /*#__PURE__*/React.createElement(Hint, {
    id: "".concat(useId, "-hint"),
    text: helpText,
    utils: {
      vrBottom: 'sm'
    }
  })));
};
var renderHints = function renderHints(errorText, useId, hasError, hasWarning, successText, warningText) {
  return /*#__PURE__*/React.createElement(React.Fragment, null, hasError ? /*#__PURE__*/React.createElement(Hint, {
    id: "".concat(useId, "-error"),
    text: errorText,
    type: "error"
  }) : '', hasWarning ? /*#__PURE__*/React.createElement(Hint, {
    type: "warning",
    id: "".concat(useId, "-warning"),
    text: warningText
  }) : '', successText ? /*#__PURE__*/React.createElement(Hint, {
    type: "success",
    id: "".concat(useId, "-success"),
    text: successText
  }) : '');
};

/**
 *
 * @ignore @description
 * ## Form Option Group Component
 * This component will render out a set of FormOptions of the same type.
 *
 * @ignore @see Mesh: [Form Option Group Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/forms-formoptiongroup--default)
 *
 * @ignore @example
 * ```jsx
 * import { FormOptionGroup } from 'mesh-component-library';
 *
 * <FormOptionGroup {...props}>
 *   {children}
 * </FormOptionGroup>
 * ```
 */
var FormOptionGroup = function FormOptionGroup(props) {
  var id = props.id,
    className = props.className,
    options = props.options,
    label = props.label,
    required = props.required,
    type = props.type,
    helpText = props.helpText,
    errorText = props.errorText,
    warningText = props.warningText,
    successText = props.successText,
    hasError = props.hasError,
    hasWarning = props.hasWarning,
    isHorizontal = props.isHorizontal,
    isInverse = props.isInverse,
    onContainerSize = props.onContainerSize,
    utils = props.utils,
    other = _objectWithoutProperties(props, _excluded);
  var formOptionGroup = useRef();
  var useId = id || generateId('form-option-group-');
  // generate ID for horizontal / vertical list
  var listId = "".concat(useId, "-list");
  var useIsInverse = React.useContext(InverseContext);
  if (isInverse !== 'auto') {
    useIsInverse = isInverse;
  }
  return /*#__PURE__*/React.createElement(StyledCacheWrapper, null, /*#__PURE__*/React.createElement(ContainerQuery, {
    onContainerSize: onContainerSize
  }, /*#__PURE__*/React.createElement(StyledFormOptionGroup, _extends({
    ref: formOptionGroup,
    className: className,
    isHorizontal: isHorizontal,
    utils: utils
  }, other), /*#__PURE__*/React.createElement("legend", null, /*#__PURE__*/React.createElement(Label, {
    text: label,
    htmlFor: useId,
    required: required,
    isInverse: useIsInverse,
    utils: (!hasError || !hasWarning) && helpText != '' ? {
      vrBottom: 'none'
    } : {
      vrBottom: 'xs'
    }
  }), renderHelp(errorText, useId, hasError, hasWarning, successText, warningText, helpText)), isHorizontal ? /*#__PURE__*/React.createElement(HorizontalList, {
    divider: "none",
    id: listId,
    spacing: "3",
    items: options.map(function (_ref, i) {
      var optionId = _ref.id,
        name = _ref.name,
        text = _ref.text,
        value = _ref.value,
        prop = _objectWithoutProperties(_ref, _excluded2);
      var key = optionId || "".concat(useId, "-input-").concat(i);
      return {
        item: /*#__PURE__*/React.createElement(FormOption, _extends({
          key: key,
          id: optionId,
          isInverse: useIsInverse,
          type: type,
          name: name,
          value: value,
          "aria-describedby": generateDescribedBy(hasError, errorText, hasWarning, successText, useId)
        }, prop), text)
      };
    })
  }) : /*#__PURE__*/React.createElement(VerticalList, {
    divider: "none",
    id: listId,
    items: options.map(function (_ref2, i) {
      var optionId = _ref2.id,
        name = _ref2.name,
        text = _ref2.text,
        value = _ref2.value,
        prop = _objectWithoutProperties(_ref2, _excluded3);
      var key = optionId || "".concat(useId, "-input-").concat(i);
      return /*#__PURE__*/React.createElement(FormOption, _extends({
        key: key,
        id: optionId,
        isInverse: useIsInverse,
        type: type,
        name: name,
        value: value,
        "aria-describedby": generateDescribedBy(hasError, errorText, hasWarning, successText, useId)
      }, prop), text);
    })
  }), renderHints(errorText, useId, hasError, hasWarning, successText, warningText))));
};
FormOptionGroup.propTypes = formOptionGroupPropTypes;
FormOptionGroup.defaultProps = formOptionGroupDefaultProps;
export default FormOptionGroup;